import { type ApolloClient } from 'apollo-client';
import gql from 'graphql-tag';

export const DEFAULT_AGENT_REFERENCE = 'ai_mate_agent';

type Agent = {
	node: {
		externalConfigReference: string | null;
		id: string;
		name: string | null;
	};
};

export const GET_ROVO_AGENTS = gql`
	query agentStudioGetAgents(
		$cloudId: String!
		$input: AgentStudioAgentQueryInput
		$product: String
	) {
		agentStudio_getAgents(cloudId: $cloudId, input: $input, product: $product)
			@optIn(to: "AgentStudio") {
			edges {
				node {
					id
					name
					externalConfigReference
				}
			}
		}
	}
`;

// From https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/06c1faa9feb39485f12c6528f9baf9bb1c653451/platform/packages/ai-mate/conversation-assistant-agent/src/services/paginated-agents/utils.tsx
const getAgentIdFromAri = (ari: string) => {
	// Pattern: ari:cloud:rovo::agent/activation/{uuid}/{uuid}
	const ariPattern =
		/^ari:cloud:rovo::agent\/activation\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/([^/]+)$/;

	const match = ari.match(ariPattern);
	if (!match) {
		return null;
	}
	// Return the second UUID (agent ID)
	return match[1];
};

const getIdFromAgent = (agent: Agent | undefined) => {
	const id = agent?.node?.id;

	if (!id) {
		return null;
	}

	return getAgentIdFromAri(agent?.node?.id);
};

const getAgentIdFromResponse = (
	agentsList: Agent[],
	agentKey?: string,
	agentAppId?: string,
): string | null => {
	// No agents found from given name so fallback to default agent
	if (agentsList.length === 0) {
		return null;
	}

	// OOTB agents can't have the same name,
	// but it can have the same name as a custom agent or a forge agent
	if (!agentKey || !agentAppId) {
		// Custom agents don't have externalConfigReference
		const agent = agentsList.find(
			(agent) =>
				agent?.node?.externalConfigReference != null &&
				!agent?.node?.externalConfigReference.includes('ari:cloud:ecosystem::extension'),
		);

		return getIdFromAgent(agent);
	}

	// Forge agents can have the same name so we have to filter further by key and appId
	// Their external config reference follows the pattern:
	// `${installationId}_ari:cloud:ecosystem::extension/${appId}/${environmentId}/static/${moduleKey}`;
	const forgeAgentRegex = new RegExp(`${agentAppId}/[^/]+/static/${agentKey}`);
	const agent = agentsList.find(
		(agent) =>
			agent?.node?.externalConfigReference &&
			forgeAgentRegex.test(agent?.node?.externalConfigReference),
	);

	return getIdFromAgent(agent);
};

export const getRovoAgentId = async ({
	client,
	cloudId,
	product,
	input,
	agentKey,
	agentAppId,
}: {
	agentAppId?: string;
	agentKey?: string;
	client: ApolloClient<object>;
	cloudId: string;
	input: { name: string };
	product: string;
}): Promise<string> => {
	const variables = { cloudId, product, input };

	try {
		const response = await client.query({
			query: GET_ROVO_AGENTS,
			variables,
			fetchPolicy: 'cache-first',
		});

		const agentsList = response.data?.agentStudio_getAgents?.edges ?? [];
		const agentId =
			getAgentIdFromResponse(agentsList, agentKey, agentAppId) ?? DEFAULT_AGENT_REFERENCE;
		return agentId;
	} catch (err) {
		return DEFAULT_AGENT_REFERENCE;
	}
};
